import * as React from 'react'
import { useAutoAnimate } from '@formkit/auto-animate/react'
import classNames from 'classnames'
import { BenchmarkingReportSettings, BenchmarkingRequestParams } from '@/hooks'
import { BenchmarkingReportView } from './BenchmarkingReportView'
import useAnalyticsStore from '@/features/Analytics/stores/useAnalyticsStore/useAnalyticsStore'
import { Timeline } from '@/utils/date'
import { InfoText } from '@/components/atoms/InfoText/InfoText'
import { useTranslation } from 'react-i18next'
import { ViewAsToggle } from '@/components/molecules/ViewAsToggle/ViewAsToggle'

type BenchmarkingReportProps = {
    filters: BenchmarkingRequestParams
    reportSettings: BenchmarkingReportSettings
    baselineYearTimeline?: Timeline
    previousYearTimeline?: Timeline
    isOpen?: boolean
    calculationMethodOffset?: number
}

export const BenchmarkingReport: React.FC<BenchmarkingReportProps> = (
    props
) => {
    const {
        filters,
        reportSettings,
        baselineYearTimeline,
        previousYearTimeline,
        isOpen = false,
        calculationMethodOffset,
    } = props
    const [animationParent] = useAutoAnimate()
    const { t } = useTranslation('translations')
    const view = useAnalyticsStore((state) => state.benchmarking.selectedView)
    const setView = useAnalyticsStore(
        (state) => state.benchmarking.setSelectedView
    )

    const toggleContainerClassName = classNames('flex items-center gap-4')

    return (
        <div
            className="grid w-full grid-cols-[100%] grid-rows-[auto_1fr] gap-6 p-6"
            ref={animationParent}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <div
                        className={classNames({
                            'transition-all duration-300 ease-out': isOpen,
                        })}
                        style={{
                            width: `${String(calculationMethodOffset)}px`,
                        }}
                    />
                    <InfoText
                        text={t('common.calculation_info.calendar_month.label')}
                        icon="info"
                        tooltipContent={t(
                            'common.calculation_info.calendar_month.tooltip'
                        )}
                    />
                </div>

                <div className={toggleContainerClassName}>
                    <ViewAsToggle
                        activeView={view}
                        onViewChange={setView}
                        data-testid="view-toggle-button"
                    />
                </div>
            </div>

            <BenchmarkingReportView
                filters={filters}
                activeView={view}
                reportSettings={reportSettings}
                baselineYearTimeline={baselineYearTimeline}
                previousYearTimeline={previousYearTimeline}
            />
        </div>
    )
}
